import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  RefreshCw, MousePointer, Eye, DollarSign, 
  Target, AlertCircle, ArrowLeft, Download,
  Play, Pause, Users, Layers, Image, Clock,
  ChevronLeft, ChevronRight, FileText, Link2, 
  Settings, Copy, Check, ExternalLink, Webhook
} from 'lucide-react';
import * as api from '../services/api';
import { toast } from 'sonner';

// Cache key constants
const CACHE_KEY = 'fb_ads_cache';

// Get cached data
const getCachedData = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Error reading cache:', e);
  }
  return null;
};

// Save data to cache
const saveCacheData = (data, datePreset) => {
  try {
    const cacheEntry = {
      ...data,
      datePreset,
      cachedAt: new Date().toISOString()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (e) {
    console.error('Error saving cache:', e);
  }
};

// Ad Thumbnail component with loading state
const AdThumbnail = ({ url, name }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);
  
  if (!url || error) {
    return (
      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center">
        <Image className="w-5 h-5 text-blue-400" />
      </div>
    );
  }
  
  return (
    <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
        </div>
      )}
      <img 
        src={url} 
        alt={name || 'Ad'}
        className={`w-full h-full object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
    </div>
  );
};

const CampaignsFacebook = () => {
  const navigate = useNavigate();
  
  // Get saved tab state from localStorage
  const getSavedTab = () => {
    try {
      return localStorage.getItem('fb_ads_main_tab') || 'campaigns';
    } catch { return 'campaigns'; }
  };
  
  const getSavedSubTab = () => {
    try {
      return localStorage.getItem('fb_ads_sub_tab') || 'campaigns';
    } catch { return 'campaigns'; }
  };
  
  // Main tab state - persisted
  const [mainTab, setMainTab] = useState(getSavedTab);
  
  // Campaign data states
  const [campaigns, setCampaigns] = useState([]);
  const [allAdsets, setAllAdsets] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [accountSummary, setAccountSummary] = useState(null);
  
  // Lead Ads states
  const [leadForms, setLeadForms] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [loadingForms, setLoadingForms] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingAdsets, setLoadingAdsets] = useState(false);
  const [loadingAds, setLoadingAds] = useState(false);
  const [error, setError] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState(getSavedSubTab);
  
  // Filter states
  const [datePreset, setDatePreset] = useState('last_30d');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  
  // Cache state
  const [cacheInfo, setCacheInfo] = useState(null);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [adsetsPage, setAdsetsPage] = useState(1);
  const [adsPage, setAdsPage] = useState(1);
  const ITEMS_PER_PAGE = 20;
  
  // Save tab state to localStorage when changed
  const handleMainTabChange = (tab) => {
    setMainTab(tab);
    try { localStorage.setItem('fb_ads_main_tab', tab); } catch {}
  };
  
  const handleSubTabChange = (tab) => {
    setActiveSubTab(tab);
    try { localStorage.setItem('fb_ads_sub_tab', tab); } catch {}
  };

  // Check configuration on mount and load from cache
  useEffect(() => {
    const init = async () => {
      try {
        const status = await api.getFacebookAdsStatus();
        setIsConfigured(status.configured);
        
        if (!status.configured) {
          setError('Facebook Ads no está configurado. Contacte al administrador.');
          return;
        }
        
        // Load from cache if available
        const cached = getCachedData();
        if (cached) {
          setCampaigns(cached.campaigns || []);
          setAccountSummary(cached.accountSummary || null);
          setAllAdsets(cached.adsets || []);
          setAllAds(cached.ads || []);
          setCacheInfo({
            cachedAt: cached.cachedAt,
            datePreset: cached.datePreset
          });
          setHasLoadedFromCache(true);
        }
        
        // Load pipelines for Lead Ads configuration
        const pipelinesData = await api.getPipelines();
        setPipelines(pipelinesData.pipelines || []);
        
      } catch (err) {
        console.error('Error checking Facebook Ads status:', err);
        setError('Error al verificar la configuración de Facebook Ads');
      }
    };
    
    init();
  }, []);

  // Load Lead Forms when Lead Ads tab is selected
  useEffect(() => {
    if (mainTab === 'leadads' && leadForms.length === 0 && isConfigured) {
      loadLeadForms();
      loadWebhookInfo();
    }
  }, [mainTab, isConfigured]);

  const loadLeadForms = async () => {
    setLoadingForms(true);
    try {
      const data = await api.getFacebookLeadForms();
      setLeadForms(data.forms || []);
    } catch (err) {
      console.error('Error loading lead forms:', err);
      toast.error('Error al cargar formularios de Lead Ads');
    } finally {
      setLoadingForms(false);
    }
  };

  const loadWebhookInfo = async () => {
    try {
      const info = await api.getFacebookWebhookInfo();
      setWebhookInfo(info);
    } catch (err) {
      console.error('Error loading webhook info:', err);
    }
  };

  // Format cache date
  const formatCacheDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load campaigns from Facebook API
  const loadFromFacebook = useCallback(async () => {
    if (!isConfigured) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const dateFrom = datePreset === 'custom' ? customDateFrom : null;
      const dateTo = datePreset === 'custom' ? customDateTo : null;
      
      const campaignsData = await api.getFacebookAdsCampaigns(datePreset, dateFrom, dateTo);
      const loadedCampaigns = campaignsData.campaigns || [];
      setCampaigns(loadedCampaigns);
      
      const summaryData = await api.getFacebookAdsAccountSummary(datePreset);
      setAccountSummary(summaryData);
      
      setCurrentPage(1);
      setAdsetsPage(1);
      setAdsPage(1);
      setAllAdsets([]);
      setAllAds([]);
      
      saveCacheData({
        campaigns: loadedCampaigns,
        accountSummary: summaryData,
        adsets: [],
        ads: []
      }, datePreset);
      
      setCacheInfo({
        cachedAt: new Date().toISOString(),
        datePreset
      });
      
      toast.success(`${loadedCampaigns.length} campañas cargadas desde Facebook`);
    } catch (err) {
      console.error('Error loading from Facebook:', err);
      setError(err.response?.data?.detail || 'Error al cargar datos de Facebook');
      toast.error('Error al cargar datos de Facebook Ads');
    } finally {
      setLoading(false);
    }
  }, [isConfigured, datePreset, customDateFrom, customDateTo]);

  // Load adsets when tab is selected - OPTIMIZED
  const loadAdsets = useCallback(async () => {
    if (allAdsets.length > 0) return;
    
    setLoadingAdsets(true);
    try {
      // Use optimized endpoint that gets ALL adsets in one call
      const data = await api.getAllFacebookAdsets(datePreset);
      setAllAdsets(data.adsets || []);
      
      const cached = getCachedData();
      if (cached) {
        saveCacheData({ ...cached, adsets: data.adsets || [] }, datePreset);
      }
      
      toast.success(`${data.adsets?.length || 0} conjuntos de anuncios cargados`);
    } catch (err) {
      console.error('Error loading adsets:', err);
      toast.error('Error al cargar conjuntos de anuncios');
    } finally {
      setLoadingAdsets(false);
    }
  }, [allAdsets.length, datePreset]);

  // Load ads when tab is selected - OPTIMIZED
  const loadAds = useCallback(async () => {
    if (allAds.length > 0) return;
    
    setLoadingAds(true);
    try {
      // Use optimized endpoint that gets ALL ads in one call
      const data = await api.getAllFacebookAds(datePreset);
      setAllAds(data.ads || []);
      
      const cached = getCachedData();
      if (cached) {
        saveCacheData({ ...cached, ads: data.ads || [] }, datePreset);
      }
      
      toast.success(`${data.ads?.length || 0} anuncios cargados`);
    } catch (err) {
      console.error('Error loading ads:', err);
      toast.error('Error al cargar anuncios');
    } finally {
      setLoadingAds(false);
    }
  }, [allAds.length, datePreset]);

  // Handle subtab change for campaigns - load data when clicking tabs
  useEffect(() => {
    if (activeSubTab === 'adsets' && allAdsets.length === 0 && !loadingAdsets) {
      loadAdsets();
    } else if (activeSubTab === 'ads' && allAds.length === 0 && !loadingAds) {
      loadAds();
    }
  }, [activeSubTab, loadAdsets, loadAds, allAdsets.length, allAds.length, loadingAdsets, loadingAds]);

  // Handle form mapping
  const handleFormMapping = async (form, pipelineId, stageId = null) => {
    try {
      const pipeline = pipelines.find(p => p.id === pipelineId);
      const stage = pipeline?.stages?.find(s => s.id === stageId);
      
      await api.saveFacebookFormMapping({
        formId: form.id,
        formName: form.name,
        pipelineId: pipelineId,
        pipelineName: pipeline?.name || '',
        stageId: stageId || pipeline?.stages?.[0]?.id || 'nuevo-lead',
        stageName: stage?.name || pipeline?.stages?.[0]?.name || 'Nuevo Lead'
      });
      
      // Update local state
      setLeadForms(prev => prev.map(f => 
        f.id === form.id 
          ? { ...f, mapping: { pipelineId, stageId, pipelineName: pipeline?.name } }
          : f
      ));
      
      toast.success(`Formulario "${form.name}" vinculado a embudo "${pipeline?.name}"`);
    } catch (err) {
      console.error('Error saving mapping:', err);
      toast.error('Error al guardar la configuración');
    }
  };

  // Remove form mapping
  const handleRemoveMapping = async (form) => {
    try {
      await api.deleteFacebookFormMapping(form.id);
      
      setLeadForms(prev => prev.map(f => 
        f.id === form.id ? { ...f, mapping: null } : f
      ));
      
      toast.success('Configuración eliminada');
    } catch (err) {
      console.error('Error removing mapping:', err);
      toast.error('Error al eliminar la configuración');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copiado al portapapeles');
  };

  // Pagination helpers
  const getPaginatedData = (data, page) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return data.slice(start, end);
  };

  const getTotalPages = (data) => Math.ceil(data.length / ITEMS_PER_PAGE);

  // Format helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('es-CO').format(value);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { label: 'Activo', color: 'bg-green-100 text-green-800', icon: Play },
      PAUSED: { label: 'Pausado', color: 'bg-yellow-100 text-yellow-800', icon: Pause },
      DELETED: { label: 'Eliminado', color: 'bg-red-100 text-red-800', icon: AlertCircle },
      ARCHIVED: { label: 'Archivado', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getObjectiveLabel = (objective) => {
    const objectives = {
      OUTCOME_AWARENESS: 'Reconocimiento',
      OUTCOME_ENGAGEMENT: 'Interacción',
      OUTCOME_LEADS: 'Clientes potenciales',
      OUTCOME_SALES: 'Ventas',
      OUTCOME_TRAFFIC: 'Tráfico',
      LEAD_GENERATION: 'Generación de leads',
    };
    return objectives[objective] || objective;
  };

  const datePresetOptions = [
    { value: 'today', label: 'Hoy' },
    { value: 'yesterday', label: 'Ayer' },
    { value: 'last_7d', label: 'Últimos 7 días' },
    { value: 'last_14d', label: 'Últimos 14 días' },
    { value: 'last_30d', label: 'Últimos 30 días' },
    { value: 'this_month', label: 'Este mes' },
    { value: 'last_month', label: 'Mes anterior' },
    { value: 'last_90d', label: 'Últimos 90 días' },
    { value: 'custom', label: 'Personalizado' }
  ];

  const campaignSubTabs = [
    { id: 'campaigns', label: 'Campañas', icon: Target, count: campaigns.length },
    { id: 'adsets', label: 'Conjuntos de Anuncios', icon: Layers, count: allAdsets.length },
    { id: 'ads', label: 'Anuncios', icon: Image, count: allAds.length }
  ];

  // Pagination component
  const Pagination = ({ currentPage, totalPages, onPageChange, totalItems }) => (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <div className="text-sm text-slate-500">
        Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm text-slate-600">Página {currentPage} de {totalPages}</span>
        <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Error state
  if (error && !isConfigured) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold">Facebook Ads</h1>
        </div>
        
        <Card className="max-w-2xl mx-auto mt-12">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Configuración Requerida</h2>
              <p className="text-slate-600 mb-4">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Facebook Ads</h1>
            <p className="text-sm text-slate-500">
              Campañas y Lead Ads de Meta (Facebook/Instagram)
            </p>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-1">
          <button
            onClick={() => handleMainTabChange('campaigns')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              mainTab === 'campaigns'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="main-tab-campaigns"
          >
            <Target className="w-4 h-4" />
            Rendimiento de Campañas
          </button>
          <button
            onClick={() => handleMainTabChange('leadads')}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              mainTab === 'leadads'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="main-tab-leadads"
          >
            <FileText className="w-4 h-4" />
            Lead Ads
            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
              Configurar
            </span>
          </button>
        </nav>
      </div>

      {/* CAMPAIGNS TAB CONTENT */}
      {mainTab === 'campaigns' && (
        <>
          {/* Filters Bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="w-44" data-testid="date-preset-select">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                {datePresetOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {datePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
                <span className="text-slate-400">a</span>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            )}
            
            <Button 
              onClick={loadFromFacebook}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="load-facebook-btn"
            >
              <Download className={`w-4 h-4 mr-2 ${loading ? 'animate-pulse' : ''}`} />
              {loading ? 'Cargando...' : 'Traer datos de Facebook'}
            </Button>
          </div>

          {/* Cache Info Banner */}
          {cacheInfo && (
            <div className="flex items-center justify-between px-4 py-3 bg-slate-100 rounded-lg text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>
                  Datos en caché del <strong>{formatCacheDate(cacheInfo.cachedAt)}</strong>
                </span>
              </div>
              <span className="text-slate-500">
                Usa "Traer datos de Facebook" para actualizar
              </span>
            </div>
          )}

          {/* No data message */}
          {!hasLoadedFromCache && campaigns.length === 0 && !loading && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center">
                  <Download className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h2 className="text-xl font-semibold text-slate-700 mb-2">Sin datos cargados</h2>
                  <p className="text-slate-500 mb-6">
                    Haz clic en "Traer datos de Facebook" para cargar las campañas
                  </p>
                  <Button onClick={loadFromFacebook} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Download className="w-4 h-4 mr-2" />
                    Traer datos de Facebook
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          {accountSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-blue-100">
                      <Eye className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Impresiones</p>
                      <p className="text-2xl font-bold">{formatNumber(accountSummary.totals.impressions)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-green-100">
                      <MousePointer className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Clics</p>
                      <p className="text-2xl font-bold">{formatNumber(accountSummary.totals.clicks)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-purple-100">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Gasto Total</p>
                      <p className="text-2xl font-bold">{formatCurrency(accountSummary.totals.spend)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-orange-100">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Alcance</p>
                      <p className="text-2xl font-bold">{formatNumber(accountSummary.totals.reach)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Campaign Data Table */}
          {campaigns.length > 0 && (
            <>
              <div className="border-b border-slate-200">
                <nav className="flex gap-1">
                  {campaignSubTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeSubTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleSubTabChange(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                          isActive ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                        <span className={`px-2 py-0.5 rounded-full text-xs ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'}`}>
                          {tab.count}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              <Card>
                <CardContent className="p-0">
                  {/* Campaigns Table */}
                  {activeSubTab === 'campaigns' && (
                    <>
                      {loading ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Campaña</TableHead>
                                  <TableHead>Objetivo</TableHead>
                                  <TableHead>Estado</TableHead>
                                  <TableHead className="text-right">Impresiones</TableHead>
                                  <TableHead className="text-right">Clics</TableHead>
                                  <TableHead className="text-right">CTR</TableHead>
                                  <TableHead className="text-right">Gasto</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getPaginatedData(campaigns, currentPage).map((campaign) => (
                                  <TableRow key={campaign.id}>
                                    <TableCell>
                                      <span className="font-medium truncate max-w-xs block">{campaign.name}</span>
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-xs bg-slate-100 px-2 py-1 rounded">
                                        {getObjectiveLabel(campaign.objective)}
                                      </span>
                                    </TableCell>
                                    <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(campaign.impressions)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(campaign.clicks)}</TableCell>
                                    <TableCell className="text-right font-mono">{campaign.ctr?.toFixed(2)}%</TableCell>
                                    <TableCell className="text-right font-mono">{formatCurrency(campaign.spend)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {getTotalPages(campaigns) > 1 && (
                            <Pagination currentPage={currentPage} totalPages={getTotalPages(campaigns)} onPageChange={setCurrentPage} totalItems={campaigns.length} />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Ad Sets Table */}
                  {activeSubTab === 'adsets' && (
                    <>
                      {loadingAdsets ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                        </div>
                      ) : allAdsets.length === 0 ? (
                        <div className="text-center py-12">
                          <Layers className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                          <p className="text-slate-500">No hay conjuntos de anuncios</p>
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Conjunto</TableHead>
                                  <TableHead>Campaña</TableHead>
                                  <TableHead>Estado</TableHead>
                                  <TableHead className="text-right">Impresiones</TableHead>
                                  <TableHead className="text-right">Clics</TableHead>
                                  <TableHead className="text-right">Gasto</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getPaginatedData(allAdsets, adsetsPage).map((adset) => (
                                  <TableRow key={adset.id}>
                                    <TableCell className="font-medium">{adset.name}</TableCell>
                                    <TableCell className="text-slate-500 text-xs">{adset.campaignName}</TableCell>
                                    <TableCell>{getStatusBadge(adset.status)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(adset.impressions)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(adset.clicks)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatCurrency(adset.spend)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {getTotalPages(allAdsets) > 1 && (
                            <Pagination currentPage={adsetsPage} totalPages={getTotalPages(allAdsets)} onPageChange={setAdsetsPage} totalItems={allAdsets.length} />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Ads Table */}
                  {activeSubTab === 'ads' && (
                    <>
                      {loadingAds ? (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                        </div>
                      ) : allAds.length === 0 ? (
                        <div className="text-center py-12">
                          <Image className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                          <p className="text-slate-500">No hay anuncios</p>
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-20">Imagen</TableHead>
                                  <TableHead>Anuncio</TableHead>
                                  <TableHead>Conjunto</TableHead>
                                  <TableHead>Estado</TableHead>
                                  <TableHead className="text-right">Impresiones</TableHead>
                                  <TableHead className="text-right">Clics</TableHead>
                                  <TableHead className="text-right">Gasto</TableHead>
                                  <TableHead className="text-center">Ver</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getPaginatedData(allAds, adsPage).map((ad) => (
                                  <TableRow key={ad.id}>
                                    <TableCell>
                                      <AdThumbnail url={ad.thumbnailUrl} name={ad.name} />
                                    </TableCell>
                                    <TableCell className="font-medium max-w-xs truncate">{ad.name}</TableCell>
                                    <TableCell className="text-slate-500 text-xs max-w-[150px] truncate">{ad.adsetName}</TableCell>
                                    <TableCell>{getStatusBadge(ad.status)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(ad.impressions)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatNumber(ad.clicks)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatCurrency(ad.spend)}</TableCell>
                                    <TableCell className="text-center">
                                      {ad.previewLink ? (
                                        <a 
                                          href={ad.previewLink} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                          title="Ver anuncio en Facebook"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      ) : (
                                        <span className="text-slate-300">-</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          {getTotalPages(allAds) > 1 && (
                            <Pagination currentPage={adsPage} totalPages={getTotalPages(allAds)} onPageChange={setAdsPage} totalItems={allAds.length} />
                          )}
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* LEAD ADS TAB CONTENT */}
      {mainTab === 'leadads' && (
        <div className="space-y-6">
          {/* Webhook Configuration Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-purple-100">
                  <Webhook className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    Configuración del Webhook
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Configura el webhook en Meta Business Suite para recibir leads automáticamente
                  </p>
                  
                  {webhookInfo && (
                    <div className="space-y-3 bg-slate-50 p-4 rounded-lg">
                      <div>
                        <label className="text-xs font-medium text-slate-500 uppercase">URL del Webhook</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded text-sm font-mono truncate">
                            {webhookInfo.webhookUrl}
                          </code>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyToClipboard(webhookInfo.webhookUrl, 'url')}
                          >
                            {copiedField === 'url' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs font-medium text-slate-500 uppercase">Token de Verificación</label>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded text-sm font-mono">
                            {webhookInfo.verifyToken}
                          </code>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => copyToClipboard(webhookInfo.verifyToken, 'token')}
                          >
                            {copiedField === 'token' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="pt-2">
                        <a 
                          href="https://business.facebook.com/settings/webhooks" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ir a Meta Business Suite - Webhooks
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Forms Mapping */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-blue-100">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      Formularios de Lead Ads
                    </h3>
                    <p className="text-sm text-slate-500">
                      Vincula cada formulario a un embudo específico
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  onClick={loadLeadForms}
                  disabled={loadingForms}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingForms ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>

              {loadingForms ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : leadForms.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h4 className="text-lg font-medium text-slate-700 mb-2">
                    No se encontraron formularios
                  </h4>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Crea un formulario de Lead Ads en Meta Ads Manager para empezar a capturar leads automáticamente.
                  </p>
                  <a 
                    href="https://www.facebook.com/adsmanager" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Ir a Meta Ads Manager
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {leadForms.map((form) => (
                    <div 
                      key={form.id}
                      className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${form.mapping ? 'bg-green-100' : 'bg-slate-100'}`}>
                          <FileText className={`w-5 h-5 ${form.mapping ? 'text-green-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">{form.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            {form.page?.name && (
                              <span>Página: {form.page.name}</span>
                            )}
                            <span>{form.leadsCount || 0} leads</span>
                            {form.mapping && (
                              <span className="flex items-center gap-1 text-green-600">
                                <Link2 className="w-3 h-3" />
                                {form.mapping.pipelineName}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Select
                          value={form.mapping?.pipelineId || ''}
                          onValueChange={(value) => handleFormMapping(form, value)}
                        >
                          <SelectTrigger className="w-56">
                            <SelectValue placeholder="Seleccionar embudo..." />
                          </SelectTrigger>
                          <SelectContent>
                            {pipelines.map(pipeline => (
                              <SelectItem key={pipeline.id} value={pipeline.id}>
                                {pipeline.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {form.mapping && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMapping(form)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Desvincular
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-amber-100">
                  <Settings className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Instrucciones de Configuración
                  </h3>
                  <ol className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-200 rounded-full text-xs font-medium">1</span>
                      <span>Ve a <strong>Meta Business Suite → Configuración → Webhooks</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-200 rounded-full text-xs font-medium">2</span>
                      <span>Selecciona tu aplicación de Facebook y haz clic en <strong>"Leadgen"</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-200 rounded-full text-xs font-medium">3</span>
                      <span>Ingresa la <strong>URL del Webhook</strong> y el <strong>Token de Verificación</strong> de arriba</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-200 rounded-full text-xs font-medium">4</span>
                      <span>Suscríbete al evento <strong>"leadgen"</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-slate-200 rounded-full text-xs font-medium">5</span>
                      <span>Vincula cada formulario al embudo donde quieres que lleguen los leads</span>
                    </li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CampaignsFacebook;
